import { useUIStore } from '../store/uiStore';
import { useDefensePlansStore } from '../store/defensePlansStore';

/**
 * Whether the read-only defense-plan viewer currently occupies the desktop
 * shell's left-docked surface (Sept 2026 — client ask: "come out from the
 * left side and cover up" the sidebar — see DockedPanel's `side` prop). The
 * sidebar itself no longer hides for a docked plan surface (editor or
 * viewer): it collapses to a 64px rail on the map screen instead and stays
 * up throughout (AppShell, SidebarNav's `variant` prop).
 *
 * This is the exact condition app/(tabs)/index.tsx computes for its own
 * `showPlanViewer` (a plan or a "that plan is gone" notice is up, and the
 * editor isn't — editing and viewing share the one surface). Lifted here so
 * anything else that needs the same condition can't drift from the screen's
 * own panel-mount logic.
 *
 * Used to return `editorOpen`/`open` too, for a sidebar-hide condition that
 * no longer exists — trimmed back to the one field an external caller
 * actually reads. `planPanelOpen` itself is still available straight from
 * uiStore for call sites that want the editor's state on its own (e.g.
 * app/(tabs)/index.tsx reads it directly for `planPanelOpen`).
 *
 * viewedPlan/viewedPlanNotice live in defensePlansStore (owned by the
 * multi-plan workstream) — this only reads them, it doesn't touch that
 * store's contract.
 */
export function usePlanSurfaceOpen(): { viewerOpen: boolean } {
  const editorOpen = useUIStore((s) => s.planPanelOpen);
  const viewedPlan = useDefensePlansStore((s) => s.viewedPlan);
  const viewedPlanNotice = useDefensePlansStore((s) => s.viewedPlanNotice);
  const viewerOpen = (viewedPlan != null || viewedPlanNotice != null) && !editorOpen;
  return { viewerOpen };
}
